import React, { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from "react";
import "swiper/css";
import "swiper/css/pagination";

import { Flex } from "antd";
import { AppContext } from "App";
import editPencil from "assets/images/new-questionnaire/edit_pencil.svg";
import QuestionnaireButtonFooterV2 from "components/basic/Footer/QuestionnaireButtonFooterV2";
import ButtonLoading from "components/basic/Spinner/ButtonLoading";
import { useModalOpener } from "hooks/helpers/useModalOpener";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { selectDocument } from "redux/reducers/document";
import { selectCurrentNavigation } from "redux/reducers/navigation";
import { setCurrentProfileAnswers } from "redux/reducers/taxProfile";
import { useGetClientTaxProfileQuery, useUpdateClientTaxProfileMutation } from "services/client";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { IUser } from "types/auth";
import { AccountDetails } from "types/bankAccounts";
import { ExpensesCategory, TaxProfile } from "types/client";
import { splitIntoCategoryChunks } from "utils/data";
import { round2Decimals } from "utils/numbers";
import { logError } from "utils/sentry";
import { cleanHTMLString } from "utils/strings";

import { CategoryEmoji, ExpenseCategoryModalData } from "../../constants/categories";
import { PromptModalDataType, Question, Step } from "../../constants/questions";
import { ParentProfileType, ProfileType, PromptModalType } from "../../enums/enum";
import ExplainerModal from "../Shared/ExplainerModal";
import InlineAmountInput from "../Shared/InlineEditableAmountInput";
import PromptModal from "../Shared/PromptModal";

type ProfileExpensesSummaryProps = {
    user: IUser;
    currentStepData: Step;
    data: Question;
    taxProfileType: ProfileType;
    taxParentProfileType: ParentProfileType;
    taxProfile: TaxProfile;
    handleContinue: () => void;
    handlePrev: () => void;
    connectedBankAccounts: AccountDetails[];
};

const getExplainerText = (taxProfileType: ProfileType, reviewableTransactionsTotal: number) => {
    switch (taxProfileType) {
    case "Self Employment":
        if (!reviewableTransactionsTotal) {
            return "Please enter the total amount for each allowable expense category related to your self-employment.";
        } else {
            return "Please review and confirm the expenses associated with this self-employment";
        }
    case "Furnished Holiday Let":
        if (!reviewableTransactionsTotal) {
            return "Please enter the total amount for each allowable expense category related to your furnished holiday let property, such as maintenance, mortgage interest, insurance, and letting agent fees.";
        } else {
            return "Please review and confirm the expenses associated with this furnished holiday let";
        }
    case "Residential/Commercial":
        if (!reviewableTransactionsTotal) {
            return "Please enter the total amount for each allowable expense category related to your residential/commercial property, such as maintenance, mortgage interest, insurance, and letting agent fees.";
        } else {
            return "Please review and confirm the expenses associated with this residential/commercial property";
        }
    case "Rent-a-Room":
        if (!reviewableTransactionsTotal) {
            return "Please enter the total amount for each allowable expense category related to your rent-a-room property, such as maintenance, mortgage interest, insurance, and letting agent fees.";
        } else {
            return "Please review and confirm the expenses associated with this rent-a-room property";
        }
    case "cis":
        if (!reviewableTransactionsTotal) {
            return "Please enter the total amount for each allowable expense category related to your CIS subcontracting work, such as tools, materials, travel, and other business costs.";
        } else {
            return "Please review and confirm the expenses associated with this CIS employment";
        }
    default:
        return "";
    }
};

const getLearnMoreText = (taxProfileType: ProfileType, reviewableTransactionsTotal: number) => {
    switch (taxProfileType) {
    case "Self Employment":
    case "cis":
        if (!reviewableTransactionsTotal) {
            return `
                <strong>Clothing</strong>
                <ul>
                    <li>Costs for protective clothing (e.g., safety boots, hard hats) and uniforms required for your work.</li>
                    <li>Includes specialist workwear for actors, performers, or tradespeople.</li>
                    <li>Laundry expenses for work-specific clothing.</li>
                </ul>
                <br/>
                <strong>Financial</strong>
                <ul>
                    <li>Bank fees, overdraft charges, and credit card fees related to your business accounts.</li>
                    <li>Interest on business loans or credit.</li>
                    <li>Accounting, bookkeeping, and tax filing service fees.</li>
                </ul>
                <br/>
                <strong>Food</strong>
                <ul>
                    <li>Food and drink expenses while working at a temporary site for under 2 years.</li>
                    <li>Meals during business travel or overnight stays for work purposes.</li>
                </ul>
                <br/>
                <strong>Accommodation </strong>(Previously hotels)
                <ul>
                    <li>Costs for hotels, B&Bs, serviced apartments, or short-term rentals for business trips.</li>
                    <li>Can be claimed when attending work events, conferences, or projects away from home.</li>
                </ul>
                <br/>
                <strong>Interest</strong>
                <ul>
                    <li>Interest payments on business loans, credit cards, or hire purchase agreements used for your work.</li>
                    <li>Excludes personal interest payments.</li>
                </ul>
                <br/>
                <strong>Marketing</strong>
                <ul>
                    <li>Interest payments on business loans, credit cards, or hire purchase agreements used for your work.</li>
                    <li>Branding expenses like logo design or business cards.</li>
                </ul>
                <br/>
                <strong>Motor</strong>
                <ul>
                    <li>Business use of your vehicle, including fuel, insurance, repairs, servicing, and road tax.</li>
                    <li>Taxzap will calculate whether using actual vehicle costs or claiming mileage using HMRC's approved rates is more beneficial.</li>
                </ul>
                <br/>
                <strong>Office</strong>
                <ul>
                    <li>Costs for renting office space, shared workspaces, or a home office.</li>
                    <li>Office supplies, equipment, and furniture directly used for your work.</li>
                </ul>
                <br/>
                <strong>Phone</strong>
                <ul>
                    <li>Business phone bills, including mobile and landline expenses.</li>
                    <li>A proportion of personal phone costs can be claimed if used for work.</li>
                </ul>
                <br/>
                <strong>Professional Services</strong>
                <ul>
                    <li>Payments for accountants, solicitors, consultants, or other professionals hired to support your business.</li>
                    <li>Includes tax advisory fees and business consulting.</li>
                </ul>
                <br/>
                <strong>Rent</strong>
                <ul>
                    <li>Rent for business premises like offices, workshops, or storage units.</li>
                    <li>If working from home, a proportion of rent can be claimed based on business use.</li>
                </ul>
                <br/>
                <strong>Repairs & Maintenance</strong>
                <ul>
                    <li>Costs for repairing and maintaining business premises, tools, or equipment.</li>
                    <li>Excludes repairs on personal property.</li>
                </ul>
                <br/>
                <strong>Staff</strong>
                <ul>
                    <li>Wages, salaries, bonuses, and National Insurance contributions for employees.</li>
                    <li>Costs of hiring freelancers, subcontractors, or temporary workers.</li>
                </ul>
                <br/>
                <strong>Tools & Materials</strong>
                <ul>
                    <li>Essential tools, materials, and equipment needed for your work.</li>
                    <li>Includes software, stationery, and resale stock.</li>
                </ul>
                <br/>
                <strong>Training</strong>
                <ul>
                    <li>Courses and certifications that maintain or improve skills directly related to your current business.</li>
                    <li>Excludes training for new, unrelated careers.</li>
                </ul>
                <br/>
                <strong>Travel</strong>
                <ul>
                    <li>Business travel expenses such as train, bus, air, or taxi fares.</li>
                    <li>Mileage if using your own vehicle, along with parking and toll fees.</li>
                    <li>Overnight accommodation and meals if travelling for business purposes.</li>
                </ul>
                <br/>
                <strong>Utilities</strong>
                <ul>
                    <li>Costs for phone, internet, electricity, heating, water, and council tax for business premises.</li>
                    <li>A proportion can be claimed for home office use.</li>
                </ul>
                <br/>
                <strong>Other</strong>
                <ul>
                    <li>Any business expenses that don't fall under other categories but are necessary for your work.</li>
                    <li>Must be exclusively for business use and well-documented.</li>
                </ul>
            `;
        } else {
            return "";
        }
    case "Furnished Holiday Let":
    case "Residential/Commercial":
    case "Rent-a-Room":
        if (!reviewableTransactionsTotal) {
            return `
                <strong>Council Tax</strong>
                <ul>
                    <li>Payments for council tax on residential properties where the landlord is responsible.</li>
                    <li>Not applicable if the tenant is responsible for council tax payments.</li>
                </ul>
                <br/>
                <strong>Financial</strong>
                <ul>
                    <li>Bank charges, loan interest, and mortgage interest payments related to the rental property.</li>
                    <li>Fees for accountants, bookkeeping, and other financial services.</li>
                </ul>
                <br/>
                <strong>Gas & Electrical Safety</strong>
                <ul>
                    <li>Costs for mandatory gas and electrical safety checks, certifications, and maintenance.</li>
                    <li>Includes compliance with legal safety requirements.</li>
                </ul>
                <br/>
                <strong>Insurance</strong>
                <ul>
                    <li>Landlord insurance, including buildings, contents (if applicable), and public liability cover.</li>
                    <li>Specialist insurance policies for rental properties.</li>
                </ul>
                <br/>
                <strong>Legal</strong>
                <ul>
                    <li>Legal fees for tenancy agreements, evictions, disputes, or seeking legal advice.</li>
                    <li>Excludes legal fees related to purchasing the property.</li>
                </ul>
                <br/>
                <strong>Management & Advertising Fees</strong>
                <ul>
                    <li>Payments to letting agents or property management companies.</li>
                    <li>Advertising costs for listing the property on rental platforms or in print.</li>
                </ul>
                <br/>
                <strong>Motor</strong>
                <ul>
                    <li>Travel expenses for managing, inspecting, or maintaining the rental property.</li>
                    <li>Can include fuel, mileage, parking fees, and public transport costs.</li>
                </ul>
                <br/>
                <strong>Other</strong>
                <ul>
                    <li>Miscellaneous expenses directly related to the rental business that don't fit into other categories.</li>
                    <li>Must be wholly and exclusively for property management purposes.</li>
                </ul>
                <br/>
                <strong>Property Visit</strong>
                <ul>
                    <li>Costs incurred when visiting the property for inspections, maintenance, or tenant management.</li>
                    <li>Includes mileage, fuel, or public transport costs, and occasional overnight stays if necessary.</li>
                </ul>
                <br/>
                <strong>Rent</strong>
                <ul>
                    <li>Rent paid if subletting a property or renting it for business purposes.</li>
                    <li>Not applicable if you own the property outright.</li>
                </ul>
                <br/>
                <strong>Repairs & Maintenance</strong>
                <ul>
                    <li>Costs for repairing and maintaining the property, including plumbing, electrical work, and general upkeep.</li>
                    <li>Includes cleaning, decorating, and minor repairs but excludes improvements that increase the property's value.</li>
                </ul>
                <br/>
                <strong>Stationery</strong>
                <ul>
                    <li>Office supplies, postage, and printing costs for managing your rental property.</li>
                    <li>Includes administrative expenses like maintaining tenant records or correspondence.</li>
                </ul>
                <br/>
                <strong>Water Charges</strong>
                <ul>
                    <li>Water, sewerage, and drainage bills for the rental property.</li>
                    <li>Claimable if the landlord covers these costs as part of the rental agreement.</li>
                </ul>
            `;
        } else {
            return "";
        }

    default:
        return "";
    }
};

const ProfileExpensesSummary: React.FC<ProfileExpensesSummaryProps> = ({
    user,
    currentStepData,
    taxProfileType,
    taxProfile,
    handleContinue,
    data,
    handlePrev,
}) => {
    const dispatch = useAppDispatch();
    const [ updateClientTaxProfile] = useUpdateClientTaxProfileMutation();
    const { stepLoading } = useAppSelector(selectCurrentNavigation);
    const { expensesExplainerShown } = useAppSelector(selectDocument);

    const { isMobile } = useContext(AppContext);
    const [, setEdit] = useState<Record<string, boolean>>({});
    const [editableCategoryAmount, setEditableCategoryAmount] = useState<Record<string, string>>({});
    const [currentProfile, setCurrentProfile] = useState<TaxProfile>(taxProfile);
    const { currentProfileAnswers } = useAppSelector(state => state.taxProfile);
    const { data: taxProfileResponse, refetch: refetchTaxProfile } = useGetClientTaxProfileQuery(
        {
            client: user?.id!,
            taxProfileId: taxProfile?._id!,
        },
        {
            skip: !user?.id || !taxProfile, // Skip the query if user or currentProfile is not available
        }
    );

    const [promptModalData, setPromptModalData] = useState<PromptModalDataType>({
        type: PromptModalType.info,
        title: "",
        text: "",
        image: "",
    });

    const [learnMoreModalData, setLearnMoreModalData] = useState<PromptModalDataType>({
        type: PromptModalType.info,
        title: "",
        text: "",
    });

    const {
        isOpened: isQuestionHelpModalOpened,
        open: handleQuestionHelpOpenModal,
        close: handleQuestionHelpCloseModal,
    } = useModalOpener();

    const {
        isOpened: isLearnMoreModalOpened,
        open: handleLearnMoreOpenModal,
        close: handleLearnMoreCloseModal,
    } = useModalOpener();

    const {
        isOpened: isExplainerModalOpened,
        open: handleExplainerOpenModal,
        close: handleExplainerCloseModal,
    } = useModalOpener();

    const categoryChunks = useMemo(() => {
        if (!currentProfile?.expenses?.category) return [];

        const categories = Object.entries(currentProfile.expenses.category);

        // Sort categories: Those with `swipedApprovedTransactionsTotal` > 0 come first
        categories.sort(([_, valueA], [__, valueB]) => {
            const hasSwipedA = valueA.swipedApprovedTransactionsTotal > 0 ? 1 : 0;
            const hasSwipedB = valueB.swipedApprovedTransactionsTotal > 0 ? 1 : 0;
            return hasSwipedB - hasSwipedA; // Prioritize categories with swipedApprovedTransactionsTotal > 0
        });

        return splitIntoCategoryChunks(Object.fromEntries(categories), categories.length);
    }, [currentProfile]);

    const canShowModal = useMemo(() => (taxProfileType === "Furnished Holiday Let" || taxProfileType === "Residential/Commercial"), [taxProfileType]);

    const editableTotalAmount = useMemo(() => {
        const totalAmount = Object.values(editableCategoryAmount).reduce((acc, value) => {
            const numericValue = parseFloat(value) || 0;
            return acc + numericValue;
        }, 0);

        return round2Decimals(totalAmount);
    }, [editableCategoryAmount]);

    const handleConfirm = useCallback(async () => {
        setEdit({});

        const updatedCategories = Object.entries(currentProfile.expenses.category).reduce((acc, [categoryName, categoryData]) => {
            acc[categoryName] = {
                ...categoryData,
                confirmedTotal: Number(editableCategoryAmount[categoryName] || "0"),
            };
            return acc;
        }, {} as Record<string, ExpensesCategory>);

        await updateClientTaxProfile({
            taxProfileId: currentProfile._id,
            client: currentProfile.client,
            expenses: {
                ...currentProfile.expenses,
                confirmedTotal: editableTotalAmount,
                category: updatedCategories,
            },

        }).unwrap()
            .then(() => {
                refetchTaxProfile?.();
                handleContinue();
            })
            .catch(error => {
                logError(error);
            });
    }, [editableCategoryAmount, handleContinue, currentProfile, updateClientTaxProfile, refetchTaxProfile, editableTotalAmount]);

    const handleModalOpen = useCallback((categoryName: string) => {
        if (canShowModal && currentStepData) {
            setPromptModalData({
                type: PromptModalType.info,
                title: CategoryEmoji[taxProfileType][categoryName] ? (CategoryEmoji[taxProfileType][categoryName] + " " + categoryName) : categoryName,
                text: ExpenseCategoryModalData[taxProfileType][categoryName]["manualExpenses"] || "",
                image: ExpenseCategoryModalData[taxProfileType][categoryName]?.image || "",
            });
            handleQuestionHelpOpenModal();
        }
    }, [canShowModal, handleQuestionHelpOpenModal, currentStepData, taxProfileType]);

    const handleLearnMore = useCallback(() => {
        setLearnMoreModalData({
            type: PromptModalType.info,
            title: "Eligible Expense Categories",
            text: cleanHTMLString(getLearnMoreText(taxProfileType, currentProfile?.expenses?.reviewableTransactionsTotal)),
        });
        handleLearnMoreOpenModal();
    }, [currentProfile?.expenses?.reviewableTransactionsTotal, handleLearnMoreOpenModal, taxProfileType]);

    const handleTotalAmountChange = (e: ChangeEvent<HTMLInputElement>, category: string) => {
        const value = e.target.value.replace(",", ".");
        if (/^\d*\.?\d{0,2}$/.test(value)) {
            setEditableCategoryAmount(prev => ({ ...prev, [category]: value }));
        }
    };

    const handleEditToggle = (categoryName: string, isEditing: boolean) => {
        setEdit(prev => ({
            ...prev,
            [categoryName]: isEditing,
        }));
    };

    useEffect(() => {
        if (currentProfile && currentProfile.expenses && currentProfile.expenses.category) {
            const initialAmounts = Object.entries(currentProfile.expenses.category).reduce((acc, [categoryName, categoryData]) => {
                acc[categoryName] = Number(categoryData.confirmedTotal || categoryData.swipedApprovedTransactionsTotal || "0.00").toFixed(2);
                return acc;
            }, {} as Record<string, string>);

            setEditableCategoryAmount(initialAmounts);
        }
    }, [currentProfile]);

    useEffect(() => {
        if (taxProfileResponse?.taxProfile) {
            setCurrentProfile(taxProfileResponse.taxProfile);
        }
    }, [taxProfileResponse]);

    useEffect(() => {
        const updatedAnswers = currentProfileAnswers.map(q => ({ ...q })); // ✅ Deep copy objects
        const existingIndex = updatedAnswers.findIndex(q => q.question === data.question);
        const updatedAnswer = { question: data.question, answer: editableTotalAmount, type: data.type || "", id: data.id || "" };
        if (existingIndex > -1) {
            updatedAnswers[existingIndex] = updatedAnswer;
        } else {
            updatedAnswers.push(updatedAnswer);
        }
        dispatch(setCurrentProfileAnswers(updatedAnswers));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editableTotalAmount]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (!expensesExplainerShown) {
                handleExplainerOpenModal();
            }
        }, 500);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        refetchTaxProfile?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (

        <div className={`new-questionnaire__income-expenses-container ${isMobile ? "" : "mt30"}`}>
            <div className="new-questionnaire__income-expenses-title taLeft mt15 w335px">{
                currentProfile?.expenses?.reviewableTransactionsTotal
                    ? "Confirm Your Expenses"
                    : "Enter Your Expenses"
            }</div>
            <div className={"new-questionnaire__income-expenses-explainer"}>
                {getExplainerText(taxProfileType, currentProfile?.expenses?.reviewableTransactionsTotal)}
                {!currentProfile?.expenses?.reviewableTransactionsTotal &&
                    ["Self Employment", "cis", "Furnished Holiday Let", "Residential/Commercial", "Rent-a-Room"].includes(taxProfileType) && (
                    <div className="new-questionnaire__income-expenses-learn-more" onClick={handleLearnMore}>
                        Learn More
                    </div>
                )}
            </div>
            <div className="new-questionnaire__income-report__wrapper m20">
                <div className={"new-questionnaire__expense-report"}>
                    <div className="w100">
                        <div className="new-questionnaire__income-report__sub-title">Total Business Expenses
                        </div>
                        <div
                            className="new-questionnaire__income-report-total-amount mb10">£{new Intl.NumberFormat("en-GB", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            }).format(parseFloat(editableTotalAmount.toString() || "0.00"))}
                        </div>
                        <Swiper
                            {...({ slidesPerView: 1 } as any)}
                            {...({ spaceBetween: 3 } as any)}
                            pagination={{ type: "fraction" }}
                            loop={true}
                            modules={[Pagination]}
                        >
                            {categoryChunks.map((categoryChunk, index) => (
                                <SwiperSlide key={index}>
                                    <div className={"new-questionnaire__expense-report-card"}>
                                        {Object.entries(categoryChunk).map(([categoryName]) => (
                                            <InlineAmountInput
                                                key={categoryName}
                                                categoryName={categoryName}
                                                categoryEmoji={CategoryEmoji[taxProfileType]?.[categoryName]}
                                                amount={editableCategoryAmount[categoryName] ?? "0.00"}
                                                canShowModal={true} // Pass conditionally if needed
                                                onAmountChange={handleTotalAmountChange}
                                                onEditToggle={handleEditToggle}
                                                onModalOpen={handleModalOpen}
                                                editIcon={editPencil}
                                            />

                                        ))}
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>

                    </div>
                </div>

                <div className="mt100">
                    <QuestionnaireButtonFooterV2>
                        <Flex justify="space-between" align="center" className="w100 p20" gap={12}>
                            <button
                                className={"shared__secondary-button"}
                                onClick={handlePrev}
                            >
                                Back
                            </button>
                            <button
                                className={`shared__primary-button maxWidth160px ${!stepLoading ? "" : "disabled"}`}
                                disabled={stepLoading}
                                onClick={handleConfirm}
                            >
                                {stepLoading ? <ButtonLoading/>
                                    : <span>{"Confirm"}</span>}
                            </button>
                        </Flex>
                    </QuestionnaireButtonFooterV2>
                </div>

                {isQuestionHelpModalOpened && (
                    <PromptModal
                        isOpen={isQuestionHelpModalOpened}
                        closeModal={handleQuestionHelpCloseModal}
                        data={promptModalData}
                        handlePrev={() => {}}
                        imageWidth={300}
                        closeButtonText={"Got It"}
                    />
                )}
                {isLearnMoreModalOpened && (
                    <PromptModal
                        isOpen={isLearnMoreModalOpened}
                        closeModal={handleLearnMoreCloseModal}
                        data={learnMoreModalData}
                        handlePrev={() => {}}
                    />
                )}
                {isExplainerModalOpened && (
                    <ExplainerModal
                        isOpen={isExplainerModalOpened}
                        closeModal={handleExplainerCloseModal}
                        subTitle={`${currentStepData.title} - ${taxProfile.name}`}
                    />
                )}
            </div>
        </div>
    );
};

export default ProfileExpensesSummary;
